import React, { useState } from "react";
import Layout from "@/components/layout/Layout";
import { SEOHead } from "@/components/seo/SEOHead";
import { generateFAQSchema, generateBreadcrumbSchema, generateHowToSchema } from "@/lib/schema";
import { useFairDealApplicationInsights } from "@/hooks/use-fair-deal-application-insights";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  ArrowRight,
  Sparkles,
  TrendingUp,
  FileText,
  Users,
  Calculator,
  Home,
  ShieldCheck,
  Clock,
  AlertTriangle,
  ExternalLink,
  AlertCircle,
  Euro,
  Search,
  Phone,
  ChevronRight,
  BookOpen,
} from "lucide-react";

// ── Application steps ──────────────────────────────────────────────────────────
const STEPS = [
  {
    id: "care-assessment",
    number: "01",
    icon: Users,
    title: "Get a Care Needs Assessment",
    summary: "The starting point for every Fair Deal application — a formal assessment by the HSE confirming that you need long-term nursing home care.",
    duration: "1–4 weeks",
    points: [
      "Contact your GP or local HSE office to request a care needs assessment",
      "A HSE assessor will visit to determine the appropriate level of care",
      "The assessment is free and required before you can apply for Fair Deal",
      "You can do this while still living at home — don't wait for a crisis",
      "Result: a formal recommendation for long-term nursing home care",
    ],
    tip: "Start this early — even if a move isn't imminent. Approval is valid for 3 months once issued, giving you time to find the right home.",
    cta: { label: "Browse HSE-approved nursing homes", href: "/nursing-homes" },
  },
  {
    id: "gather-documents",
    number: "02",
    icon: FileText,
    title: "Gather Your Financial Documents",
    summary: "The HSE financial assessment (NF1 form) requires detailed proof of all income and assets. Missing documents are the #1 cause of delays.",
    duration: "1–2 weeks",
    points: [
      "Pension statements from the Department of Social Protection",
      "Bank statements for all accounts for the last 3 years",
      "A licensed auctioneer's valuation of any property owned",
      "PPS numbers for the applicant and their spouse / civil partner",
      "Marriage or civil partnership certificate (if applicable)",
      "Details of any assets transferred in the last 5 years (the look-back period)",
    ],
    tip: "Get the property valuation from a licensed auctioneer before submitting — the HSE will not accept self-valuations.",
    cta: null,
  },
  {
    id: "submit-application",
    number: "03",
    icon: BookOpen,
    title: "Submit the NHSS Application (NF1 Form)",
    summary: "Complete and submit the Nursing Homes Support Scheme (NHSS) application form — available from your local HSE office or online at hse.ie.",
    duration: "2–6 weeks for HSE review",
    points: [
      "Download or collect the NF1 application pack from hse.ie or your local HSE office",
      "The pack includes a care needs section and a financial assessment section",
      "Submit to your Local Placement Forum (LPF) — listed on the application form",
      "The HSE will acknowledge receipt and issue a reference number",
      "A HSE officer will review and may request additional documentation",
    ],
    tip: "Keep a full copy of everything you submit. Note the date and get a receipt or confirmation of submission.",
    cta: {
      label: "Download NF1 form (hse.ie)",
      href: "https://www.hse.ie/eng/services/list/4/olderpeople/nhss/",
      external: true,
    },
  },
  {
    id: "financial-assessment",
    number: "04",
    icon: Calculator,
    title: "Financial Assessment & Contribution",
    summary: "The HSE calculates your personal weekly contribution based on 80% of income and 7.5% of assets per year — with the 3-year property cap.",
    duration: "Within overall processing time",
    points: [
      "80% of all assessable weekly income (pension, rental income, etc.) is your contribution",
      "7.5% per year of your non-income assets (savings, property) is added",
      "Your principal private residence is capped at 3 years (22.5% maximum)",
      "After 3 years, the state covers the property portion indefinitely",
      "You can opt to defer the property contribution — the HSE places a charge on the property, recovered after death",
      "A couple where one person enters care: only 50% of joint income and assets are assessed",
    ],
    tip: "Use the Grandpal Fair Deal calculator to estimate your contribution before the HSE assessment arrives.",
    cta: { label: "Calculate your contribution", href: "/fair-deal" },
  },
  {
    id: "choose-home",
    number: "05",
    icon: Home,
    title: "Choose a Fair Deal-Approved Home",
    summary: "Once approval is issued (valid for 3 months), you can choose any HIQA-registered, NTPF-approved nursing home anywhere in Ireland.",
    duration: "Your timeline",
    points: [
      "Search Grandpal to compare Fair Deal-approved homes by county, cost, and amenities",
      "The home must be HIQA-registered and have an NTPF-negotiated weekly rate",
      "If the home charges above the NTPF rate, you may be asked to pay a 'top-up'",
      "Check waiting lists — popular homes in Dublin and Cork can have significant wait times",
      "Your Fair Deal approval is portable — you can choose any participating home in Ireland",
    ],
    tip: "Visit at least 2–3 homes before deciding. Arrive unannounced for a second visit to see the home as it really is.",
    cta: { label: "Search Fair Deal-approved homes", href: "/nursing-homes" },
  },
  {
    id: "approval-move-in",
    number: "06",
    icon: ShieldCheck,
    title: "Receive Approval & Move In",
    summary: "Once you confirm a placement, the HSE formalises the arrangement. The state pays its share directly to the nursing home — you pay yours.",
    duration: "Ongoing",
    points: [
      "Notify the HSE of your chosen nursing home and your admission date",
      "The HSE issues a formal 'negotiated price' confirmation to the home",
      "Your personal contribution is paid directly to the nursing home (often via pension redirect)",
      "The state's contribution is paid directly to the nursing home by the HSE",
      "You can review your financial assessment annually if circumstances change",
      "Fair Deal continues indefinitely — there is no lifetime cap on duration",
    ],
    tip: "Keep records of your admission date — the 3-year property cap clock starts from the date you enter full-time care.",
    cta: { label: "Book a consultation with a care advisor", href: "/consultations" },
  },
];

// ── FAQs ───────────────────────────────────────────────────────────────────────
const FAQS = [
  {
    question: "When should I start the Fair Deal application?",
    answer:
      "As early as possible — ideally months before a nursing home place is needed. The process takes 4–12 weeks, and many families apply while their loved one is still living at home or in hospital. Fair Deal approval is valid for 3 months once issued, giving you time to find the right home without rushing.",
  },
  {
    question: "What is the 5-year look-back rule?",
    answer:
      "The HSE assesses any assets that were transferred (gifted or sold at below market value) in the 5 years before applying. If assets were moved within that window, they may still be counted in your financial assessment. This prevents people from gifting assets to reduce their contribution.",
  },
  {
    question: "Can I defer the property contribution?",
    answer:
      "Yes. The HSE offers a 'Nursing Homes Loan' which defers the property-related portion of your contribution. Instead of paying it weekly, the HSE registers a charge on the property and recovers it from the estate after death (or when the property is sold). Interest applies, so it's worth taking financial advice on this.",
  },
  {
    question: "What happens if my income or assets change after approval?",
    answer:
      "You should notify the HSE of any significant changes (e.g. the death of a spouse, sale of property, or change in pension income). Your financial assessment can be reviewed annually, or at any time if there's a material change in your circumstances.",
  },
  {
    question: "What if my chosen nursing home charges more than the NTPF rate?",
    answer:
      "Some nursing homes charge a 'top-up' above the NTPF-negotiated rate. This top-up is paid by you (or your family) in addition to your Fair Deal personal contribution. Grandpal shows the NTPF rate for each listed home so you can compare before visiting.",
  },
  {
    question: "Can I change nursing homes after Fair Deal is approved?",
    answer:
      "Yes — your Fair Deal approval follows you to any other HIQA-registered, NTPF-approved home. Contact the HSE's NHSS office to update your placement. You'll need to give appropriate notice to your current home as per your contract.",
  },
  {
    question: "Is there a waiting list for Fair Deal?",
    answer:
      "There is no national waiting list for Fair Deal approval itself — the HSE processes applications in order. However, individual nursing homes (especially in Dublin, Cork, and Galway) can have waiting lists for beds. It's worth applying to multiple homes simultaneously once approved.",
  },
  {
    question: "Does Fair Deal cover dementia or specialist care?",
    answer:
      "Yes — Fair Deal covers all long-term nursing home care, including dementia care, palliative care, and high-dependency care. The care needs assessment determines the appropriate level of care required, and Fair Deal covers the cost of an NTPF-approved home providing that level.",
  },
];

// ── Live Application Stats Panel ───────────────────────────────────────────────
function LiveApplicationStatsPanel() {
  const { insights, citations, isLoading, error } = useFairDealApplicationInsights();

  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary shrink-0" />
          <h3 className="text-base font-semibold text-foreground">
            Live 2026 Fair Deal Application Stats
          </h3>
        </div>
        <Badge variant="secondary" className="self-start sm:ml-auto text-xs">
          AI-powered
        </Badge>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </div>
      ) : error || !insights ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm p-3 rounded-lg bg-muted">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>Live data temporarily unavailable.</span>
        </div>
      ) : (
        <>
          {insights.keyUpdate2026 && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-primary/5 border border-primary/15">
              <TrendingUp className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <p className="text-xs sm:text-sm text-foreground">{insights.keyUpdate2026}</p>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
            {[
              { label: "Processing Time", value: insights.processingWeeks },
              { label: "Approval Rate", value: insights.approvalRate },
              { label: "Avg. Contribution", value: insights.avgWeeklyContribution },
              { label: "Homes on Scheme", value: insights.homesOnScheme },
              { label: "Property Cap", value: insights.propertyCapYears },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg border border-border bg-background p-3"
              >
                <p className="text-xs text-muted-foreground">{stat.label}</p>
                <p className="text-base sm:text-lg font-bold text-foreground mt-0.5">
                  {stat.value}
                </p>
              </div>
            ))}
          </div>

          {citations.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1 border-t border-border">
              {citations.slice(0, 4).map((c, i) => {
                try {
                  const domain = new URL(c).hostname.replace("www.", "");
                  return (
                    <a
                      key={i}
                      href={c}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      {domain}
                    </a>
                  );
                } catch {
                  return null;
                }
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Documents checklist (dynamic from AI) ─────────────────────────────────────
function DocumentsChecklist() {
  const { insights, isLoading } = useFairDealApplicationInsights();
  const [checked, setChecked] = useState<Record<number, boolean>>({});

  const docs =
    insights?.documentsNeeded ?? [
      "Proof of income — pension payslips, DSP statements",
      "Bank statements for all accounts (last 3 years)",
      "Property valuation from a licensed auctioneer",
      "PPS numbers for applicant and spouse / civil partner",
      "Marriage or civil partnership certificate",
      "Details of assets transferred in the last 5 years",
    ];

  const toggle = (i: number) =>
    setChecked((prev) => ({ ...prev, [i]: !prev[i] }));
  const allChecked = docs.every((_, i) => checked[i]);
  const count = docs.filter((_, i) => checked[i]).length;

  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          Documents Checklist
        </h3>
        <Badge
          variant={allChecked ? "default" : "secondary"}
          className="text-xs"
        >
          {count}/{docs.length} ready
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground">
        Tick each item as you gather it. Having everything ready before you
        submit avoids weeks of back-and-forth with the HSE.
      </p>

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-10 rounded-lg" />
          ))}
        </div>
      ) : (
        <ul className="space-y-2">
          {docs.map((doc, i) => (
            <li key={i}>
              <button
                onClick={() => toggle(i)}
                className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-colors ${
                  checked[i]
                    ? "border-primary/30 bg-primary/5"
                    : "border-border bg-background hover:bg-muted/30"
                }`}
              >
                <CheckCircle2
                  className={`h-4 w-4 mt-0.5 shrink-0 transition-colors ${
                    checked[i] ? "text-primary" : "text-muted-foreground/40"
                  }`}
                />
                <span
                  className={`text-sm ${
                    checked[i]
                      ? "line-through text-muted-foreground"
                      : "text-foreground"
                  }`}
                >
                  {doc}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {allChecked && (
        <div className="p-3 rounded-xl bg-primary/10 border border-primary/20 text-center">
          <p className="text-sm font-medium text-primary">
            ✓ All documents ready — you're set to submit!
          </p>
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function FairDealApplicationGuidePage() {
  const [activeStep, setActiveStep] = useState<string | null>("care-assessment");

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Guides", url: "/guides" },
    {
      name: "Fair Deal Application Guide 2026",
      url: "/guides/fair-deal-application-ireland-2026",
    },
  ];

  const schemas = [
    generateBreadcrumbSchema(breadcrumbs),
    generateFAQSchema(FAQS),
    generateHowToSchema({
      name: "How to Apply for Fair Deal (NHSS) in Ireland 2026",
      description:
        "A complete step-by-step guide to applying for the Irish Fair Deal nursing home support scheme in 2026 — from care needs assessment to moving in.",
      steps: STEPS.map((s) => ({ name: s.title, text: s.summary })),
      totalTime: "PT8W",
    }),
  ];

  return (
    <Layout>
      <SEOHead
        title="How to Apply for Fair Deal in Ireland 2026 | Step-by-Step Guide | Grandpal"
        description="Complete 2026 guide to the Fair Deal (NHSS) application process in Ireland. 6 clear steps, document checklist, financial assessment explained. Updated with live data."
        canonicalUrl="/guides/fair-deal-application-ireland-2026"
        schema={schemas}
      />

      {/* ── Hero ── */}
      <section className="bg-gradient-to-b from-primary/5 to-background py-10 sm:py-16 md:py-20 border-b border-border">
        <div className="container max-w-4xl px-4">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge variant="secondary" className="text-xs">
              Step-by-Step Guide · 2026
            </Badge>
            <Badge variant="outline" className="text-xs text-muted-foreground">
              Updated with live data
            </Badge>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground leading-tight mb-4">
            How to Apply for Fair Deal
            <br className="hidden sm:block" /> in Ireland 2026
          </h1>
          <p className="text-base sm:text-xl text-muted-foreground max-w-2xl mb-6">
            The Fair Deal (NHSS) application can take 4–12 weeks. This guide
            walks you through every step — documents, forms, the financial
            assessment, and choosing a nursing home.
          </p>

          {/* Quick jump nav */}
          <div className="flex flex-wrap gap-2 mb-8">
            {STEPS.map((step) => (
              <button
                key={step.id}
                onClick={() => {
                  setActiveStep(step.id);
                  document
                    .getElementById(step.id)
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-border bg-background text-xs font-medium hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
              >
                {step.number} {step.title}
              </button>
            ))}
          </div>

          {/* Hero CTAs */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button asChild size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/fair-deal">
                <Calculator className="h-4 w-4 mr-2" />
                Calculate Your Contribution
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full sm:w-auto"
            >
              <Link to="/nursing-homes">
                <Search className="h-4 w-4 mr-2" />
                Find Approved Homes
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <div className="container max-w-4xl px-4 py-8 sm:py-12 md:py-16 space-y-10 sm:space-y-14">

        {/* Live stats */}
        <LiveApplicationStatsPanel />

        {/* Feature strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: Clock, label: "4–12 week process", desc: "Start early to avoid delays" },
            { icon: CheckCircle2, label: "Interactive checklist", desc: "Track your documents as you go" },
            { icon: Euro, label: "Live 2026 figures", desc: "Contribution rates & stats" },
          ].map((item) => (
            <div
              key={item.label}
              className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card"
            >
              <item.icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-foreground">{item.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Steps ── */}
        <div className="space-y-5 sm:space-y-6">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground">
            The 6-Step Application Process
          </h2>

          {STEPS.map((step) => {
            const Icon = step.icon;
            const isOpen = activeStep === step.id;
            return (
              <section
                key={step.id}
                id={step.id}
                className="rounded-2xl border border-border bg-card overflow-hidden scroll-mt-20"
              >
                {/* Step header */}
                <button
                  onClick={() => setActiveStep(isOpen ? null : step.id)}
                  className="w-full text-left p-4 sm:p-6 flex items-start gap-4 hover:bg-muted/30 transition-colors"
                >
                  <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-muted-foreground">
                        {step.number}
                      </span>
                      <h3 className="text-base sm:text-xl font-bold text-foreground">
                        {step.title}
                      </h3>
                      <Badge
                        variant="outline"
                        className="text-xs hidden sm:inline-flex"
                      >
                        <Clock className="h-2.5 w-2.5 mr-1" />
                        {step.duration}
                      </Badge>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {step.summary}
                    </p>
                    <Badge
                      variant="outline"
                      className="text-xs mt-2 sm:hidden"
                    >
                      <Clock className="h-2.5 w-2.5 mr-1" />
                      {step.duration}
                    </Badge>
                  </div>
                  <ArrowRight
                    className={`h-4 w-4 text-muted-foreground shrink-0 mt-1 transition-transform duration-200 ${
                      isOpen ? "rotate-90" : ""
                    }`}
                  />
                </button>

                {/* Expandable content */}
                {isOpen && (
                  <div className="px-4 sm:px-6 pb-5 sm:pb-6 border-t border-border">
                    <ul className="space-y-3 mt-4 sm:mt-5">
                      {step.points.map((point, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground leading-relaxed">
                            {point}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* Pro tip */}
                    {step.tip && (
                      <div className="mt-4 flex items-start gap-2 p-3 sm:p-4 rounded-xl bg-muted border border-border">
                        <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <p className="text-xs sm:text-sm text-foreground">
                          <span className="font-semibold">Tip: </span>
                          {step.tip}
                        </p>
                      </div>
                    )}

                    {/* CTA */}
                    {step.cta && (
                      <div className="mt-4">
                        {step.cta.external ? (
                          <a
                            href={step.cta.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                          >
                            {step.cta.label}
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        ) : (
                          <Button asChild variant="outline" size="sm">
                            <Link to={step.cta.href}>
                              {step.cta.label}
                              <ChevronRight className="h-3.5 w-3.5 ml-1" />
                            </Link>
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>

        {/* ── Interactive documents checklist ── */}
        <DocumentsChecklist />

        {/* ── Common mistakes ── */}
        <CommonMistakesPanel />

        {/* ── CTA — Fair Deal calculator ── */}
        <section className="rounded-2xl bg-primary p-6 sm:p-8 md:p-10 text-primary-foreground text-center space-y-4">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold">
            Estimate your contribution before applying
          </h2>
          <p className="text-primary-foreground/80 text-sm sm:text-base max-w-xl mx-auto">
            Use the Grandpal Fair Deal calculator to get an indicative weekly
            contribution figure based on your income and assets — before the
            HSE's formal assessment.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              asChild
              size="lg"
              variant="secondary"
              className="w-full sm:w-auto font-semibold"
            >
              <Link to="/fair-deal">
                <Calculator className="h-4 w-4 mr-2" />
                Open the Calculator
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
            >
              <Link to="/consultations">
                <Phone className="h-4 w-4 mr-2" />
                Book a Free Consultation
              </Link>
            </Button>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section id="faq">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-4 sm:mb-6">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-border rounded-xl px-4 sm:px-5 py-1 bg-card"
              >
                <AccordionTrigger className="text-sm sm:text-base font-medium text-foreground text-left hover:no-underline py-3 sm:py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* ── Related guides ── */}
        <section className="border-t border-border pt-8">
          <h2 className="text-base font-semibold text-foreground mb-4">
            Related guides
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link
              to="/guides/finding-nursing-home-ireland-2026"
              className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card hover:bg-muted/30 transition-colors group"
            >
              <BookOpen className="h-5 w-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  Finding a Nursing Home in Ireland 2026
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Complete guide to choosing the right home
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
            </Link>
            <Link
              to="/fair-deal"
              className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card hover:bg-muted/30 transition-colors group"
            >
              <Euro className="h-5 w-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  Fair Deal Hub — Live Rates & Calculator
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  NTPF rates, contribution calculator, and more
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
}

// ── Common Mistakes Panel (uses AI data) ──────────────────────────────────────
function CommonMistakesPanel() {
  const { insights, isLoading } = useFairDealApplicationInsights();

  const mistakes =
    insights?.commonMistakes ?? [
      "Applying too late — start months before a placement is needed",
      "Forgetting to include all bank accounts and savings in the assessment",
      "Not getting a formal property valuation before submitting",
      "Missing the 5-year look-back period for asset transfers",
    ];

  return (
    <div className="rounded-2xl border border-border bg-muted/40 p-4 sm:p-6 space-y-4">
      <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-primary shrink-0" />
        Common Application Mistakes to Avoid
      </h3>
      {isLoading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-8 rounded-lg" />
          ))}
        </div>
      ) : (
        <ul className="space-y-2.5">
          {mistakes.map((mistake, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span className="shrink-0 w-5 h-5 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center mt-0.5">
                {i + 1}
              </span>
              <span className="text-sm text-foreground leading-relaxed">
                {mistake}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
